import clsx from 'clsx';

export const DEAL_TABS = ['signal', 'model'] as const;
export type DealTab = (typeof DEAL_TABS)[number];

const TAB_LABELS: Record<DealTab, string> = {
  signal: 'Signal',
  model: 'Model',
};

export interface DealTabBarProps {
  activeTab: DealTab;
  onChange: (next: DealTab) => void;
  /**
   * When true, the Signal tab renders disabled + carries a tooltip. The
   * default-tab decision (`/deal/<id>` without `?tab=` flips to Model)
   * lives in the parent route — `<DealTabBar>` is presentation-only.
   */
  signalDisabled?: boolean;
  className?: string;
}

/**
 * `external-sharing` Phase 5 Session 5.3 (2026-05-09).
 *
 * 2-tab strip (Signal | Model) for `/deal/:shareId`. Mirrors the
 * inline tab strip in `<ReadOnlyShareBuilder>` (and `routes/builder.tsx`)
 * — same density, accent underline on active, `data-testid` selectors
 * scoped to the deal namespace. Workbench is intentionally NOT exposed
 * on the public surface (anonymous viewers don't talk to the agent;
 * recipients who want to interact must Fork into their own Builder
 * conversation, which lives behind auth).
 *
 * `signalDisabled` dims the Signal tab + carries a tooltip when the
 * share's pinned model has `signal_variant === null`. The parent
 * route's defaulting logic flips to Model in that case so the page
 * still shows useful content (the spreadsheet view) instead of a
 * "Deal Not Viewable" placeholder.
 */
export function DealTabBar({
  activeTab,
  onChange,
  signalDisabled,
  className,
}: DealTabBarProps) {
  return (
    <div
      data-testid="deal-tab-bar"
      role="tablist"
      aria-label="Public deal view"
      className={clsx(
        'flex items-center gap-1 px-5 pt-3 border-b border-panel-border bg-bg shrink-0',
        className,
      )}
    >
      {DEAL_TABS.map((t) => {
        const disabled = t === 'signal' && signalDisabled === true;
        const tooltip = disabled
          ? "This model doesn't have a Signal view configured."
          : undefined;
        return (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={activeTab === t}
            data-disabled={disabled ? 'true' : undefined}
            disabled={disabled}
            title={tooltip}
            onClick={() => {
              if (disabled) return;
              onChange(t);
            }}
            data-testid={`deal-tab-${t}`}
            className={clsx(
              'px-4 py-1.5 text-sm font-mono cursor-pointer transition-colors -mb-px border-b-2 bg-transparent',
              activeTab === t
                ? 'text-accent border-accent'
                : 'text-text-muted hover:text-text border-transparent',
              disabled && 'opacity-40 cursor-not-allowed hover:text-text-muted',
            )}
          >
            {TAB_LABELS[t]}
          </button>
        );
      })}
    </div>
  );
}
